import {
  EXPORT_FIELD,
  EXPORT_FORMAT,
  EXPORT_LIMITS,
  EXPORT_SECTION,
  EXPORT_SECTION_ORDER,
  EXPORT_VERSION,
  META_FIELD,
} from './catalog';
import type { CsvRow } from './csv';
import { encodeCsv, parseCsv } from './csv';
import type { ExportDocument } from './document';
import { rowsToDocument } from './document';
import { EXPORT_ISSUE, issueFor } from './issue';
import { fail, ok } from './result';
import type { ExportResult } from './result';

/**
 * The export file as a whole: what goes in it, and what comes back out.
 *
 * The codec is deliberately two-sided and knows nothing about the app. A caller
 * supplies the rows of the sections it can fill — the RCU definition through
 * `rcu.ts`, the keypad book through the console's own seam — and this module
 * frames them with the `meta` section, writes them in catalog order, and reads
 * any file back into sections.
 *
 * That split is what makes the format extensible: a new kind of record is a new
 * section in `catalog.ts`, rows from whoever owns it, and a decoder to read them.
 * Nothing in here changes.
 */

/** The rows a caller contributes, by section. Absent sections are written empty. */
export type SectionRows = Partial<Record<string, readonly CsvRow[]>>;

export interface BuildExportInput {
  /**
   * Rows per section, each already including its own header row.
   *
   * One entry per contributor rather than one merged map, so the RCU half and
   * the keypad half can each hand over what they own without either needing to
   * know what the other writes.
   */
  readonly sections: readonly SectionRows[];
  /** ISO time the file was written; also the `meta` row's own value. */
  readonly exportedAt: string;
}

/**
 * Frames the caller's rows into a complete document.
 *
 * Every section is written even when it holds nothing, because the file is a
 * snapshot of the whole tablet: a reader that finds no `rail` section cannot
 * tell "no rails fitted" from "this file was truncated", and writing the header
 * removes the question.
 *
 * A section claimed by two contributors is a mistake in the caller — the second
 * would silently double the first's rows — so the later one wins only for the
 * sections it actually holds, and a section nobody claims is declared empty.
 */
export const buildExport = (input: BuildExportInput): string => {
  const claimed = new Map<string, readonly CsvRow[]>();
  for (const contribution of input.sections) {
    for (const [section, rows] of Object.entries(contribution)) {
      if (rows !== undefined && rows.length > 0) claimed.set(section, rows);
    }
  }

  const header: CsvRow = [EXPORT_SECTION.META, ...EXPORT_FIELD[EXPORT_SECTION.META]];
  const meta: CsvRow = [
    EXPORT_SECTION.META,
    EXPORT_FORMAT,
    String(EXPORT_VERSION),
    input.exportedAt,
  ];

  const body = EXPORT_SECTION_ORDER.flatMap((section): readonly CsvRow[] => {
    if (section === EXPORT_SECTION.META) return [header, meta];
    const rows = claimed.get(section);
    // A section nobody contributed is still declared, with its header alone.
    return rows !== undefined ? rows : [[section, ...EXPORT_FIELD[section]]];
  });

  return encodeCsv(body);
};

/** A file that was read and understood, before anything has been applied. */
export interface ReadExport {
  readonly document: ExportDocument;
}

/**
 * Parses and structurally checks a file.
 *
 * This is the point at which a foreign, truncated or hand-mangled file is
 * refused — before any of it is believed. What the sections *mean* is still the
 * caller's question: the RCU half and the keypad half are decoded separately, so
 * a bad load is reported as a bad load rather than as an unreadable file.
 *
 * The size is checked here rather than by the caller so there is one answer to
 * "how big is too big", and it is checked before the text is scanned.
 */
export const readExport = (text: string, byteLength: number): ExportResult<ReadExport> => {
  if (byteLength > EXPORT_LIMITS.MAX_FILE_BYTES) {
    return fail(issueFor(EXPORT_ISSUE.FILE_TOO_LARGE, describeBytes(byteLength)));
  }

  const parsed = parseCsv(text);
  if (!parsed.ok) return fail(parsed.issue);

  const document = rowsToDocument(parsed.value);
  if (!document.ok) return fail(document.issue);

  return ok({ document: document.value });
};

/** The export timestamp a document carries, or an empty string. */
export const exportedAtOf = (document: ExportDocument): string => document.exportedAt;

/** The format marker a document carries, for a caller checking what it read. */
export const formatOf = (document: ExportDocument): string => document.format;

/** A byte count as something a technician can read, e.g. `4.0 MB`. */
export const describeBytes = (bytes: number): string =>
  `${(bytes / EXPORT_LIMITS.BYTES_PER_KB / EXPORT_LIMITS.BYTES_PER_KB).toFixed(
    EXPORT_LIMITS.SIZE_DECIMALS,
  )} MB`;

/** Re-exported so a caller needs one import for the format's own constants. */
export { EXPORT_FORMAT, EXPORT_LIMITS, EXPORT_SECTION, EXPORT_VERSION, META_FIELD };
